import { FunctionComponent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { SmoMoonList } from '../../../../Models/Smo/SmoMoonList';
import MoonListsActions from '../../../../Modules/Redux/Pages/MoonLists/MoonListsActions';
import MoonListsSelectors from '../../../../Modules/Redux/Pages/MoonLists/MoonListsSelectors';
import { ReactComponent } from '../../../../Utils/Types';
import { Box } from '../../Components/Box/Box';
import { CenterContent } from '../../Components/CenterContent/CenterContent';
import { Grid } from '../../Components/Grid/Grid';
import { LoadingIndicator } from '../../Components/LoadingIndicator/LoadingIndicator';
import { NavigationBar } from '../../Components/NavigationBar/NavigationBar';
import { PageLayout } from '../../Components/PageLayout/PageLayout';
import { PageSection } from '../../Components/PageSection/PageSection';
import { SearchFieldSuggestionItem } from '../../Components/SearchField/Models/SearchFieldSuggestionItem';
import { SearchField } from '../../Components/SearchField/SearchField';
import { Switch } from '../../Components/Switch/Switch';
import { SwitchCase } from '../../Components/SwitchCase/SwitchCase';
import { TabBar } from '../../Components/TabBar/TabBar';
import { TabItem } from '../../Components/TabItem/TabItem';
import { MoonListCell } from './Components/MoonListCell/MoonListCell';

export const MoonListsPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allMoonLists: SmoMoonList[] = useSelector(MoonListsSelectors.all);
  const popularMoonLists: SmoMoonList[] = useSelector(MoonListsSelectors.popular);
  const search: string = useSelector(MoonListsSelectors.search);
  const searchResults: SmoMoonList[] = useSelector(MoonListsSelectors.searchResults);
  const state = useSelector(MoonListsSelectors.state);

  useEffect(() => {
    dispatch(MoonListsActions.getMoonLists());
    return (): void => {
      dispatch(MoonListsActions.resetState());
    };
  }, []);

  function didSearch(search: string): void {
    dispatch(MoonListsActions.setSearch(search));
    if (search.length === 0) {
      dispatch(MoonListsActions.setSearchResults([]));
    }
  }

  function createMoonList(): void {
    navigate('/moon/list/create');
  }

  function suggestionItemForMoonList(moonList: SmoMoonList): SearchFieldSuggestionItem {
    return {
      id: moonList.id,
      item: itemForMoonList(moonList)
    };
  }

  function itemForMoonList(moonList: SmoMoonList): ReactComponent {
    return (
      <div className="items-start v-stack">
        <div>{moonList.name}</div>
        <div className="text-sm text-text-secondary">{moonList.author.login}</div>
      </div>
    );
  }

  function didSelectMoonListWithId(moonListId: string): void {
    navigate(`/moon/list/${moonListId}`);
  }

  return (
    <div className="relative">
      <Switch value={state.current}>
        <SwitchCase value={'loading'}>
          <CenterContent className="w-full h-screen">
            <Box>
              <div className="p-4">
                <LoadingIndicator />
              </div>
            </Box>
          </CenterContent>
        </SwitchCase>

        <SwitchCase value={'data'}>
          <img className="object-cover w-full" style={{ height: '10vmax' }} src="/images/smo/world_select.jpg" />

          <NavigationBar title="Moon lists">
            <TabBar>
              <TabItem title="Create" icon="/images/icons/add_red.svg" onClick={createMoonList} />
            </TabBar>
          </NavigationBar>

          <PageSection>
            <PageLayout>
              <SearchField
                value={search}
                suggestions={{
                  items: searchResults.map(suggestionItemForMoonList),
                  onSelection: didSelectMoonListWithId
                }}
                onSearch={didSearch}
              />
            </PageLayout>
          </PageSection>

          <div className="pt-4 mb-8 bg-gray-100">
            <PageLayout>
              <PageSection title="Popular" subtitle="The most liked moon lists of the moment.">
                <Grid minColumnWidth="100px" maxColumnWidth="200px">
                  {popularMoonLists.map((moonList) => (
                    <Link key={moonList.id} to={`/moon/list/${moonList.id}`}>
                      <MoonListCell moonList={moonList} />
                    </Link>
                  ))}
                </Grid>
              </PageSection>
            </PageLayout>
          </div>

          <PageLayout>
            <PageSection title="All">
              <Grid minColumnWidth="100px" maxColumnWidth="200px">
                {allMoonLists.map((moonList) => (
                  <Link key={moonList.id} to={`/moon/list/${moonList.id}`}>
                    <MoonListCell moonList={moonList} />
                  </Link>
                ))}
              </Grid>
            </PageSection>
          </PageLayout>
        </SwitchCase>
      </Switch>
    </div>
  );
};
